// src/pages/MorningBrief.jsx
// Daily Brief - Comprehensive overnight scoring report for agents
// Shows everything the risk scoring system found overnight

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sun,
  Moon,
  Cloud,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Phone,
  MessageSquare,
  Users,
  Activity,
  Heart,
  Pill,
  DollarSign,
  Calendar,
  Clock,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  ExternalLink,
  Award,
  Target,
  BarChart3,
  Bell,
  ClipboardList,
  Sparkles,
  Eye,
  X,
  Zap,
  Mail,
  MessageSquare as MessageSquareIcon,
  ToggleLeft,
  ToggleRight,
  Trash2
} from 'lucide-react';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip
} from 'recharts';
import api, { getTodayAutomationPreview, cancelQueuedAutomation, getOvernightJobs, runAllOvernightJobs, deleteOvernightJob, getScoringDetails } from '../api';

const BASE = process.env.REACT_APP_API_URL || "";

// Risk tier colors
const RISK_COLORS = {
  stable: '#22c55e',
  low: '#84cc16',
  moderate: '#eab308',
  elevated: '#f97316',
  high: '#ef4444',
  critical: '#dc2626',
  severe: '#991b1b'
};

// Priority colors
const PRIORITY_COLORS = {
  emergency: { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: 'text-red-600' },
  urgent: { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', icon: 'text-orange-600' },
  high: { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', icon: 'text-yellow-600' },
  normal: { bg: 'bg-blue-100', text: 'text-blue-800', border: 'border-blue-300', icon: 'text-blue-600' }
};

export default function MorningBrief() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    noContact: false,
    moversUp: false,
    moversDown: false,
    healthEvents: false
  });

  // Drill-down modal state
  const [drillDownModal, setDrillDownModal] = useState(null);

  // Founder testing state
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [testResults, setTestResults] = useState(null);

  // Today's scheduled automations state
  const [todayAutomations, setTodayAutomations] = useState([]);
  const [automationsLoading, setAutomationsLoading] = useState(false);
  const [cancellingId, setCancellingId] = useState(null);

  // Founder overnight job report state
  const [overnightJobs, setOvernightJobs] = useState(null);
  const [overnightJobsLoading, setOvernightJobsLoading] = useState(false);

  useEffect(() => {
    fetchUser();
    fetchBriefData();
    fetchTodayAutomations();
  }, []);

  async function fetchTodayAutomations() {
    setAutomationsLoading(true);
    try {
      const result = await getTodayAutomationPreview();
      if (result.ok) {
        setTodayAutomations(result.automations || []);
      }
    } catch (e) {
      console.error('Error fetching today automations:', e);
    } finally {
      setAutomationsLoading(false);
    }
  }

  async function handleCancelAutomation(automationId) {
    if (!window.confirm('Cancel this scheduled message? The client will not receive it.')) return;

    setCancellingId(automationId);
    try {
      const result = await cancelQueuedAutomation(automationId, 'Cancelled from Daily Brief');
      if (result.ok) {
        // Remove from local state
        setTodayAutomations(prev => prev.filter(a => a.id !== automationId));
      }
    } catch (e) {
      console.error('Error cancelling automation:', e);
      alert('Failed to cancel: ' + e.message);
    } finally {
      setCancellingId(null);
    }
  }

  useEffect(() => {
    if (user?.role === 'founder') {
      fetchAgentsList();
      fetchOvernightJobs();
    }
  }, [user]);

  async function fetchOvernightJobs() {
    setOvernightJobsLoading(true);
    try {
      const result = await getOvernightJobs();
      if (result.ok) {
        setOvernightJobs(result);
      }
    } catch (e) {
      console.error('Error fetching overnight jobs:', e);
    } finally {
      setOvernightJobsLoading(false);
    }
  }

  async function fetchUser() {
    try {
      const res = await fetch(`${BASE}/auth/me`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) setUser(json.user);
    } catch (e) {
      console.error('Error fetching user:', e);
    }
  }

  async function fetchBriefData(agentId = null) {
    setLoading(true);
    setError(null);
    try {
      const endpoint = agentId
        ? `${BASE}/morning-brief/agent/${agentId}`
        : `${BASE}/morning-brief`;
      const res = await fetch(endpoint, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) {
        setData(json.data);
      } else {
        setError(json.error || 'Failed to load Daily Brief');
      }
    } catch (e) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  }

  async function fetchAgentsList() {
    try {
      const res = await fetch(`${BASE}/morning-brief/agents`, { credentials: 'include' });
      const json = await res.json();
      if (json.ok) {
        setAgents(json.agents || []);
      }
    } catch (e) {
      console.error('Error fetching agents:', e);
    }
  }

  async function generateForAllAgents() {
    setGenerating(true);
    try {
      const res = await fetch(`${BASE}/morning-brief/generate-all`, {
        method: 'POST',
        credentials: 'include'
      });
      const json = await res.json();
      if (json.ok) {
        setTestResults(json);
      }
    } catch (e) {
      console.error('Error generating:', e);
    } finally {
      setGenerating(false);
    }
  }

  function handleViewAgentBrief(agentId) {
    setSelectedAgent(agentId);
    fetchBriefData(agentId);
  }

  function toggleSection(section) {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  }

  function openDrillDown(type, title, items) {
    setDrillDownModal({ type, title, items });
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-3 text-gray-600">
          <RefreshCw size={24} className="animate-spin" />
          <span className="text-lg">Loading Daily Brief...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <AlertCircle size={48} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to Load Brief</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => fetchBriefData()}
            className="px-4 py-2 bg-[#172A3A] text-white rounded-lg hover:bg-[#1e3a4f]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { greeting, scoreChanges, healthEvents, overnightItems, yesterdayActivity, riskDistribution } = data || {};

  return (
    <div className="min-h-screen bg-gray-50 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#172A3A] to-[#1e3a4f] text-white px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-2">
            <Sun size={28} className="text-yellow-300" />
            <h1 className="text-2xl sm:text-3xl font-bold">
              {greeting?.message || 'Good Morning'}, {greeting?.userName || 'Agent'}
            </h1>
          </div>
          <p className="text-blue-200 text-sm sm:text-base">{greeting?.date}</p>

          {/* AI Summary */}
          {greeting?.aiSummary && (
            <div className="mt-4 bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="flex items-start gap-3">
                <Sparkles size={20} className="text-yellow-300 flex-shrink-0 mt-0.5" />
                <p className="text-white/90 text-sm sm:text-base leading-relaxed">
                  {greeting.aiSummary}
                </p>
              </div>
            </div>
          )}

          {/* Back button if viewing another agent */}
          {selectedAgent && user?.role === 'founder' && (
            <button
              onClick={() => { setSelectedAgent(null); fetchBriefData(); }}
              className="mt-4 flex items-center gap-2 text-blue-200 hover:text-white text-sm"
            >
              <ChevronRight size={16} className="rotate-180" />
              Back to your brief
            </button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 -mt-4">
        {/* Founder Testing Panel */}
        {user?.role === 'founder' && !selectedAgent && (
          <FounderTestingPanel
            agents={agents}
            generating={generating}
            testResults={testResults}
            onGenerateAll={generateForAllAgents}
            onViewAgent={handleViewAgentBrief}
          />
        )}

        {/* Founder Overnight Job Report */}
        {user?.role === 'founder' && !selectedAgent && (
          <OvernightJobReport
            overnightJobs={overnightJobs}
            loading={overnightJobsLoading}
            onRefresh={fetchOvernightJobs}
            onRunAll={async () => {
              await runAllOvernightJobs();
              // Poll for results as jobs complete
              setTimeout(() => { fetchOvernightJobs(); fetchTodayAutomations(); }, 3000);
              setTimeout(() => { fetchOvernightJobs(); fetchTodayAutomations(); }, 15000);
              setTimeout(() => { fetchOvernightJobs(); fetchTodayAutomations(); }, 45000);
              setTimeout(() => { fetchOvernightJobs(); fetchTodayAutomations(); }, 90000);
            }}
          />
        )}

        {/* Today's Scheduled Automations - Always visible */}
        <section className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl shadow-md p-4 sm:p-6 mb-6 mt-8">
          <h2 className="text-lg sm:text-xl font-semibold text-amber-900 mb-4 flex items-center gap-2">
            <Zap size={20} className="text-amber-500" />
            Automations Going Out at 1 PM Today
            <span className="ml-2 bg-amber-100 text-amber-800 text-sm px-2 py-0.5 rounded-full">
              {todayAutomations.length} scheduled
            </span>
          </h2>

          {automationsLoading ? (
            <div className="flex items-center gap-2 text-amber-700 py-4">
              <RefreshCw size={16} className="animate-spin" />
              <span className="text-sm">Loading scheduled automations...</span>
            </div>
          ) : todayAutomations.length === 0 ? (
            <div className="flex items-center gap-3 py-4 px-3 bg-white/60 rounded-lg">
              <CheckCircle2 size={20} className="text-amber-400" />
              <p className="text-sm text-amber-800">No automations scheduled for today. Birthday, welcome, and OEP messages are queued at 2 AM when matching clients are found.</p>
            </div>
          ) : (
            <>
              {/* Summary by type */}
              <div className="flex flex-wrap gap-2 mb-4">
                {Object.entries(
                  todayAutomations.reduce((acc, a) => {
                    acc[a.automation_name] = (acc[a.automation_name] || 0) + 1;
                    return acc;
                  }, {})
                ).map(([name, count]) => (
                  <span key={name} className="px-2 py-1 bg-white rounded-lg text-sm text-gray-700 border">
                    {name}: <strong>{count}</strong>
                  </span>
                ))}
              </div>

              {/* Automation list */}
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {todayAutomations.map((auto) => (
                  <div
                    key={auto.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-amber-100 hover:border-amber-300 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${
                        auto.channel === 'sms' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                      }`}>
                        {auto.channel === 'sms' ? <MessageSquareIcon size={16} /> : <Mail size={16} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{auto.clientName}</p>
                        <p className="text-xs text-gray-500 truncate">
                          {auto.automation_name} • {auto.channel.toUpperCase()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <div className="relative group">
                        <button className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
                          <Eye size={16} />
                        </button>
                        <div className="absolute right-0 bottom-full mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                          {auto.preview}
                        </div>
                      </div>
                      <button
                        onClick={() => handleCancelAutomation(auto.id)}
                        disabled={cancellingId === auto.id}
                        className="px-2 py-1 text-xs text-red-600 hover:bg-red-50 rounded border border-red-200 hover:border-red-300 disabled:opacity-50"
                      >
                        {cancellingId === auto.id ? 'Cancelling...' : 'Cancel'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <p className="text-xs text-amber-700 mt-3">
                These messages will be sent automatically at 1:00 PM Eastern. Click "Cancel" to prevent sending.
              </p>
            </>
          )}
        </section>

        {/* Score Changes Summary */}
        <section className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[#172A3A] mb-4 flex items-center gap-2">
            <Activity size={20} />
            Overnight Risk Score Changes
          </h2>

          {/* Summary Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6">
            <StatCard
              label="Total Scored"
              value={scoreChanges?.summary?.totalScored || 0}
              icon={Users}
              color="blue"
            />
            <StatCard
              label="Scores Increased"
              value={scoreChanges?.summary?.scoresUp || 0}
              subtitle={`avg +${scoreChanges?.summary?.avgIncrease || 0}`}
              icon={TrendingUp}
              color="red"
            />
            <StatCard
              label="Scores Decreased"
              value={scoreChanges?.summary?.scoresDown || 0}
              subtitle={`avg -${scoreChanges?.summary?.avgDecrease || 0}`}
              icon={TrendingDown}
              color="green"
            />
            <StatCard
              label="No Change"
              value={scoreChanges?.summary?.noChange || 0}
              icon={Target}
              color="gray"
            />
          </div>

          {/* No-Contact Increases */}
          <GroupedSection
            title="Daily No-Contact Score Increases (+0.25/day)"
            icon={Clock}
            count={scoreChanges?.noContactIncreases?.length || 0}
            expanded={expandedSections.noContact}
            onToggle={() => toggleSection('noContact')}
            onViewAll={() => openDrillDown('noContact', 'No-Contact Score Increases', scoreChanges?.noContactIncreases)}
            colorClass="bg-orange-50 border-orange-200"
          >
            {scoreChanges?.noContactIncreases?.slice(0, 5).map((client, idx) => (
              <ClientRow
                key={idx}
                client={client}
                detail={`${client.daysSinceContact} days`}
                badge={`+0.25`}
                score={client.newScore}
                onCall={() => navigate(`/risk?clientId=${client.clientId}`)}
              />
            ))}
          </GroupedSection>

          {/* Biggest Movers Up */}
          {scoreChanges?.biggestMovers?.increases?.length > 0 && (
            <GroupedSection
              title="Significant Score Increases"
              icon={TrendingUp}
              count={scoreChanges.biggestMovers.increases.length}
              expanded={expandedSections.moversUp}
              onToggle={() => toggleSection('moversUp')}
              onViewAll={() => openDrillDown('moversUp', 'Score Increases', scoreChanges.biggestMovers.increases)}
              colorClass="bg-red-50 border-red-200"
            >
              {scoreChanges.biggestMovers.increases.slice(0, 5).map((client, idx) => (
                <ClientRow
                  key={idx}
                  client={client}
                  detail={client.primaryReason}
                  badge={`+${client.change}`}
                  score={client.currentScore}
                  previousScore={client.previousScore}
                  urgent={client.change >= 20}
                  onCall={() => navigate(`/risk?clientId=${client.clientId}`)}
                />
              ))}
            </GroupedSection>
          )}

          {/* Biggest Movers Down (Wins) */}
          {scoreChanges?.biggestMovers?.decreases?.length > 0 && (
            <GroupedSection
              title="Score Decreases (Wins!)"
              icon={TrendingDown}
              count={scoreChanges.biggestMovers.decreases.length}
              expanded={expandedSections.moversDown}
              onToggle={() => toggleSection('moversDown')}
              colorClass="bg-green-50 border-green-200"
            >
              {scoreChanges.biggestMovers.decreases.slice(0, 5).map((client, idx) => (
                <ClientRow
                  key={idx}
                  client={client}
                  detail={client.primaryReason}
                  badge={`${client.change}`}
                  score={client.currentScore}
                  previousScore={client.previousScore}
                  positive
                  onCall={() => navigate(`/risk?clientId=${client.clientId}`)}
                />
              ))}
            </GroupedSection>
          )}

          {/* Threshold Crossings */}
          <ThresholdCrossings crossings={scoreChanges?.thresholdCrossings} navigate={navigate} />
        </section>

        {/* Health Events */}
        <section className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[#172A3A] mb-4 flex items-center gap-2">
            <Heart size={20} className="text-red-500" />
            Health Events Detected (Blue Button)
          </h2>

          <HealthEventsPanel events={healthEvents} navigate={navigate} />
        </section>

        {/* Tasks & Alerts */}
        <section className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[#172A3A] mb-4 flex items-center gap-2">
            <ClipboardList size={20} />
            Tasks & Alerts Generated Overnight
            <span className="ml-2 bg-[#172A3A] text-white text-xs px-2 py-0.5 rounded-full">
              {overnightItems?.totals?.tasks || 0} tasks
            </span>
          </h2>

          <TasksPanel tasks={overnightItems?.tasks} alerts={overnightItems?.alerts} navigate={navigate} />
        </section>

        {/* Yesterday's Activity */}
        <section className="bg-white rounded-xl shadow-md p-4 sm:p-6 mb-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[#172A3A] mb-4 flex items-center gap-2">
            <Calendar size={20} />
            Yesterday's Activity
            {yesterdayActivity?.date && (
              <span className="text-sm font-normal text-gray-500 ml-2">
                {new Date(yesterdayActivity.date).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
              </span>
            )}
          </h2>

          <YesterdayActivity activity={yesterdayActivity} />
        </section>

        {/* Risk Distribution */}
        <section className="bg-white rounded-xl shadow-md p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-[#172A3A] mb-4 flex items-center gap-2">
            <BarChart3 size={20} />
            Book of Business Health
            <span className="ml-2 text-sm font-normal text-gray-500">
              {riskDistribution?.total || 0} total clients
            </span>
          </h2>

          <RiskDistributionChart distribution={riskDistribution} />
        </section>
      </div>

      {/* Drill-Down Modal */}
      {drillDownModal && (
        <DrillDownModal
          title={drillDownModal.title}
          items={drillDownModal.items}
          type={drillDownModal.type}
          onClose={() => setDrillDownModal(null)}
          navigate={navigate}
        />
      )}
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function StatCard({ label, value, subtitle, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    orange: 'bg-orange-50 text-orange-600',
    gray: 'bg-gray-50 text-gray-600'
  };

  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${colorClasses[color]} flex items-center justify-center`}>
          <Icon size={16} />
        </div>
        <span className="text-sm text-gray-600">{label}</span>
      </div>
      <div className="text-2xl font-bold text-[#172A3A]">{value}</div>
      {subtitle && <div className="text-xs text-gray-500 mt-1">{subtitle}</div>}
    </div>
  );
}

function GroupedSection({ title, icon: Icon, count, expanded, onToggle, onViewAll, colorClass, children }) {
  if (count === 0) return null;

  return (
    <div className={`border rounded-xl p-4 mb-4 ${colorClass}`}>
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={onToggle}
      >
        <div className="flex items-center gap-2">
          <Icon size={18} />
          <span className="font-medium text-gray-800">{title}</span>
          <span className="bg-white px-2 py-0.5 rounded-full text-sm font-semibold">
            {count} clients
          </span>
        </div>
        <div className="flex items-center gap-2">
          {count > 5 && onViewAll && (
            <button
              onClick={(e) => { e.stopPropagation(); onViewAll(); }}
              className="text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              View All <ExternalLink size={14} />
            </button>
          )}
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expanded && (
        <div className="mt-4 space-y-2">
          {children}
        </div>
      )}
    </div>
  );
}

function ClientRow({ client, detail, badge, score, previousScore, urgent, positive, onCall }) {
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg bg-white ${urgent ? 'ring-2 ring-red-300' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">{client.name}</div>
        <div className="text-sm text-gray-500">{detail}</div>
      </div>
      <div className="flex items-center gap-3">
        {previousScore !== undefined && (
          <span className="text-sm text-gray-400">{previousScore} →</span>
        )}
        <span className={`px-2 py-1 rounded text-sm font-medium ${
          positive ? 'bg-green-100 text-green-800' :
          urgent ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'
        }`}>
          {badge}
        </span>
        <span className={`font-semibold ${
          score >= 80 ? 'text-red-600' :
          score >= 65 ? 'text-orange-600' :
          score >= 50 ? 'text-yellow-600' : 'text-green-600'
        }`}>
          {score}
        </span>
        <button
          onClick={onCall}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
          title="View Profile"
        >
          <Eye size={18} />
        </button>
      </div>
    </div>
  );
}

function ThresholdCrossings({ crossings, navigate }) {
  if (!crossings) return null;

  const { critical, high, elevated } = crossings;
  const hasAny = (critical?.length > 0) || (high?.length > 0) || (elevated?.length > 0);

  if (!hasAny) return null;

  return (
    <div className="border border-red-200 rounded-xl p-4 bg-red-50">
      <h3 className="font-medium text-red-800 mb-3 flex items-center gap-2">
        <AlertTriangle size={18} />
        Clients Who Crossed Risk Thresholds
      </h3>

      {critical?.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-semibold text-red-700 mb-2">
            CRITICAL (80+) - Call Today ({critical.length})
          </div>
          {critical.map((c, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg mb-1">
              <div>
                <span className="font-medium">{c.name}</span>
                <span className="text-gray-500 text-sm ml-2">
                  ({c.previousScore}→{c.currentScore}) - {c.reason}
                </span>
              </div>
              <button
                onClick={() => navigate(`/risk?clientId=${c.clientId}`)}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Call Now
              </button>
            </div>
          ))}
        </div>
      )}

      {high?.length > 0 && (
        <div className="mb-4">
          <div className="text-sm font-semibold text-orange-700 mb-2">
            HIGH (65-79) - Call Within 48h ({high.length})
          </div>
          {high.map((c, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg mb-1">
              <div>
                <span className="font-medium">{c.name}</span>
                <span className="text-gray-500 text-sm ml-2">
                  ({c.previousScore}→{c.currentScore}) - {c.reason}
                </span>
              </div>
              <button
                onClick={() => navigate(`/risk?clientId=${c.clientId}`)}
                className="px-3 py-1 bg-orange-600 text-white text-sm rounded-lg hover:bg-orange-700"
              >
                View
              </button>
            </div>
          ))}
        </div>
      )}

      {elevated?.length > 0 && (
        <div>
          <div className="text-sm font-semibold text-yellow-700 mb-2">
            ELEVATED (50-64) - Schedule This Week ({elevated.length})
          </div>
          <div className="text-sm text-gray-600">
            {elevated.length} clients entered elevated risk zone
          </div>
        </div>
      )}
    </div>
  );
}

function HealthEventsPanel({ events, navigate }) {
  if (!events || events.totals?.total === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Heart size={32} className="mx-auto mb-2 opacity-50" />
        <p>No health events detected overnight</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* ER/Hospital Events */}
      {events.erVisits?.length > 0 && (
        <div className="border border-red-300 rounded-xl p-4 bg-red-50">
          <h3 className="font-medium text-red-800 mb-3 flex items-center gap-2">
            <AlertCircle size={18} />
            ER/Hospital Events ({events.erVisits.length})
          </h3>
          {events.erVisits.map((e, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-white rounded-lg mb-2">
              <div>
                <div className="font-medium">{e.name}</div>
                <div className="text-sm text-gray-500">{e.changeType?.replace(/_/g, ' ')}</div>
              </div>
              <button
                onClick={() => navigate(`/risk?clientId=${e.clientId}`)}
                className="px-3 py-1 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
              >
                Call Now
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Medication Changes */}
      {events.medications?.length > 0 && (
        <div className="border border-purple-200 rounded-xl p-4 bg-purple-50">
          <h3 className="font-medium text-purple-800 mb-3 flex items-center gap-2">
            <Pill size={18} />
            New Medications ({events.medications.length})
          </h3>
          {events.medications.slice(0, 5).map((e, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg mb-1">
              <div>
                <span className="font-medium">{e.name}</span>
                <span className="text-gray-500 text-sm ml-2">- {e.drugName || 'New medication'}</span>
              </div>
              <button
                onClick={() => navigate(`/risk?clientId=${e.clientId}`)}
                className="text-purple-600 hover:text-purple-800"
              >
                <Eye size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Adherence Gaps */}
      {events.adherenceGaps?.length > 0 && (
        <div className="border border-orange-200 rounded-xl p-4 bg-orange-50">
          <h3 className="font-medium text-orange-800 mb-3 flex items-center gap-2">
            <AlertTriangle size={18} />
            Adherence Gaps ({events.adherenceGaps.length})
          </h3>
          {events.adherenceGaps.slice(0, 5).map((e, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg mb-1">
              <div>
                <span className="font-medium">{e.name}</span>
                <span className="text-gray-500 text-sm ml-2">- {e.drugName || 'Medication gap'}</span>
              </div>
              <button
                onClick={() => navigate(`/risk?clientId=${e.clientId}`)}
                className="text-orange-600 hover:text-orange-800"
              >
                <Eye size={18} />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Cost Events */}
      {events.costEvents?.length > 0 && (
        <div className="border border-yellow-200 rounded-xl p-4 bg-yellow-50">
          <h3 className="font-medium text-yellow-800 mb-3 flex items-center gap-2">
            <DollarSign size={18} />
            Cost Events ({events.costEvents.length})
          </h3>
          {events.costEvents.slice(0, 5).map((e, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg mb-1">
              <div>
                <span className="font-medium">{e.name}</span>
                <span className="text-gray-500 text-sm ml-2">- {e.changeType?.replace(/_/g, ' ')}</span>
              </div>
              <button
                onClick={() => navigate(`/risk?clientId=${e.clientId}`)}
                className="text-yellow-600 hover:text-yellow-800"
              >
                <Eye size={18} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TasksPanel({ tasks, alerts, navigate }) {
  const taskGroups = [
    { key: 'emergency', label: 'EMERGENCY (Due Today)', color: PRIORITY_COLORS.emergency, items: tasks?.emergency || [] },
    { key: 'urgent', label: 'URGENT (Due 24h)', color: PRIORITY_COLORS.urgent, items: tasks?.urgent || [] },
    { key: 'high', label: 'HIGH (Due 48h)', color: PRIORITY_COLORS.high, items: tasks?.high || [] },
    { key: 'normal', label: 'NORMAL', color: PRIORITY_COLORS.normal, items: tasks?.normal || [] }
  ];

  const hasAnyTasks = taskGroups.some(g => g.items.length > 0);

  if (!hasAnyTasks && (!alerts || alerts.length === 0)) {
    return (
      <div className="text-center py-8 text-gray-500">
        <CheckCircle2 size={32} className="mx-auto mb-2 text-green-500" />
        <p>No new tasks or alerts overnight</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {taskGroups.map(group => group.items.length > 0 && (
        <div key={group.key} className={`border ${group.color.border} rounded-xl p-4 ${group.color.bg}`}>
          <h3 className={`font-medium ${group.color.text} mb-3 flex items-center gap-2`}>
            <ClipboardList size={18} className={group.color.icon} />
            {group.label} ({group.items.length})
          </h3>
          {group.items.slice(0, 5).map((task, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 bg-white rounded-lg mb-1">
              <div className="flex items-center gap-2">
                <input type="checkbox" className="w-4 h-4 rounded" />
                <div>
                  <div className="font-medium text-sm">{task.title}</div>
                  {task.name && <div className="text-xs text-gray-500">{task.name}</div>}
                </div>
              </div>
              {task.clientId && (
                <button
                  onClick={() => navigate(`/risk?clientId=${task.clientId}`)}
                  className={`px-3 py-1 text-white text-sm rounded-lg ${
                    group.key === 'emergency' ? 'bg-red-600 hover:bg-red-700' :
                    group.key === 'urgent' ? 'bg-orange-600 hover:bg-orange-700' :
                    'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {group.key === 'emergency' || group.key === 'urgent' ? 'Call' : 'View'}
                </button>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

function YesterdayActivity({ activity }) {
  if (!activity) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Calendar size={32} className="mx-auto mb-2 opacity-50" />
        <p>No activity data available</p>
      </div>
    );
  }

  const { calls, sms, tasks, clientsContacted, wins } = activity;

  return (
    <div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 rounded-xl p-4 text-center">
          <Phone size={24} className="mx-auto mb-2 text-blue-600" />
          <div className="text-2xl font-bold text-[#172A3A]">{calls?.outbound || 0}</div>
          <div className="text-sm text-gray-600">Calls Made</div>
          <div className="text-xs text-gray-500">{calls?.answered || 0} answered</div>
        </div>
        <div className="bg-green-50 rounded-xl p-4 text-center">
          <MessageSquare size={24} className="mx-auto mb-2 text-green-600" />
          <div className="text-2xl font-bold text-[#172A3A]">{sms?.sent || 0}</div>
          <div className="text-sm text-gray-600">SMS Sent</div>
          <div className="text-xs text-gray-500">{sms?.delivered || 0} delivered</div>
        </div>
        <div className="bg-purple-50 rounded-xl p-4 text-center">
          <CheckCircle2 size={24} className="mx-auto mb-2 text-purple-600" />
          <div className="text-2xl font-bold text-[#172A3A]">{tasks?.completed || 0}</div>
          <div className="text-sm text-gray-600">Tasks Done</div>
          <div className="text-xs text-gray-500">{tasks?.rate || 0}% completion</div>
        </div>
        <div className="bg-orange-50 rounded-xl p-4 text-center">
          <Users size={24} className="mx-auto mb-2 text-orange-600" />
          <div className="text-2xl font-bold text-[#172A3A]">{clientsContacted || 0}</div>
          <div className="text-sm text-gray-600">Clients Contacted</div>
        </div>
      </div>

      {/* Wins */}
      {wins && wins.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <h3 className="font-medium text-green-800 mb-3 flex items-center gap-2">
            <Award size={18} />
            Wins Yesterday
          </h3>
          {wins.map((win, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 bg-white rounded-lg mb-1">
              <CheckCircle2 size={16} className="text-green-600" />
              <span className="font-medium">{win.name}</span>
              <span className="text-gray-500 text-sm">- {win.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function RiskDistributionChart({ distribution }) {
  if (!distribution) return null;

  const data = [
    { name: 'Severe (90+)', value: distribution.severe?.count || 0, color: RISK_COLORS.severe },
    { name: 'Critical (80-89)', value: distribution.critical?.count || 0, color: RISK_COLORS.critical },
    { name: 'High (65-79)', value: distribution.high?.count || 0, color: RISK_COLORS.high },
    { name: 'Elevated (50-64)', value: distribution.elevated?.count || 0, color: RISK_COLORS.elevated },
    { name: 'Moderate (25-49)', value: distribution.moderate?.count || 0, color: RISK_COLORS.moderate },
    { name: 'Low (15-24)', value: distribution.low?.count || 0, color: RISK_COLORS.low },
    { name: 'Stable (<15)', value: distribution.stable?.count || 0, color: RISK_COLORS.stable }
  ].filter(d => d.value > 0);

  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Pie Chart */}
      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              outerRadius={80}
              label={({ name, value }) => `${value}`}
            >
              {data.map((entry, index) => (
                <Cell key={index} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>
      </div>

      {/* Legend Table */}
      <div className="space-y-2">
        {[
          { label: 'Severe (90+)', key: 'severe', color: RISK_COLORS.severe },
          { label: 'Critical (80-89)', key: 'critical', color: RISK_COLORS.critical },
          { label: 'High (65-79)', key: 'high', color: RISK_COLORS.high },
          { label: 'Elevated (50-64)', key: 'elevated', color: RISK_COLORS.elevated },
          { label: 'Moderate (25-49)', key: 'moderate', color: RISK_COLORS.moderate },
          { label: 'Low (15-24)', key: 'low', color: RISK_COLORS.low },
          { label: 'Stable (<15)', key: 'stable', color: RISK_COLORS.stable }
        ].map(tier => (
          <div key={tier.key} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tier.color }} />
              <span className="text-sm">{tier.label}</span>
            </div>
            <div className="flex items-center gap-4">
              <span className="font-semibold">{distribution[tier.key]?.count || 0}</span>
              <span className="text-xs text-gray-500 w-12 text-right">
                {distribution.total > 0
                  ? `${((distribution[tier.key]?.count || 0) / distribution.total * 100).toFixed(1)}%`
                  : '0%'
                }
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FounderTestingPanel({ agents, generating, testResults, onGenerateAll, onViewAgent }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 mb-6 mt-8">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h3 className="font-semibold text-purple-900 flex items-center gap-2">
          <Sparkles size={18} className="text-purple-600" />
          Founder Testing Panel
        </h3>
        {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </div>

      {expanded && (
        <div className="mt-4">
          <div className="flex gap-3 mb-4">
            <button
              onClick={onGenerateAll}
              disabled={generating}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
            >
              {generating ? <RefreshCw size={16} className="animate-spin" /> : <Activity size={16} />}
              Generate for All Agents
            </button>
          </div>

          {testResults && (
            <div className="bg-white rounded-lg p-3 mb-4">
              <div className="text-sm text-gray-600 mb-2">
                Generated at: {new Date(testResults.generatedAt).toLocaleString()}
              </div>
              <div className="text-sm font-medium text-green-600">
                {testResults.totalAgents} agents processed
              </div>
            </div>
          )}

          {/* Agents Table */}
          <div className="bg-white rounded-lg overflow-hidden overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left p-3 font-medium">Agent</th>
                  <th className="text-center p-3 font-medium">Clients</th>
                  <th className="text-center p-3 font-medium">Critical</th>
                  <th className="text-center p-3 font-medium">High</th>
                  <th className="text-right p-3 font-medium">Action</th>
                </tr>
              </thead>
              <tbody>
                {agents.map(agent => (
                  <tr key={agent.id} className="border-t hover:bg-gray-50">
                    <td className="p-3">
                      <div className="font-medium">{agent.name}</div>
                      <div className="text-xs text-gray-500">{agent.email}</div>
                    </td>
                    <td className="text-center p-3">{agent.client_count || 0}</td>
                    <td className="text-center p-3">
                      <span className={agent.critical_count > 0 ? 'text-red-600 font-semibold' : ''}>
                        {agent.critical_count || 0}
                      </span>
                    </td>
                    <td className="text-center p-3">
                      <span className={agent.high_count > 0 ? 'text-orange-600 font-semibold' : ''}>
                        {agent.high_count || 0}
                      </span>
                    </td>
                    <td className="text-right p-3">
                      <button
                        onClick={() => onViewAgent(agent.id)}
                        className="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                      >
                        View Brief
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function OvernightJobReport({ overnightJobs, loading, onRefresh, onRunAll }) {
  const [expanded, setExpanded] = useState(true);
  const [runningAll, setRunningAll] = useState(false);
  const [expandedJobId, setExpandedJobId] = useState(null);
  const [scoringDetails, setScoringDetails] = useState(null);
  const [scoringDetailsLoading, setScoringDetailsLoading] = useState(false);
  const [scoringSearch, setScoringSearch] = useState('');
  const [expandedClientId, setExpandedClientId] = useState(null);

  async function handleExpandJob(jobId, jobKey) {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      return;
    }
    setExpandedJobId(jobId);
    if (jobKey === 'nightly' && !scoringDetails) {
      setScoringDetailsLoading(true);
      try {
        const data = await getScoringDetails();
        setScoringDetails(data);
      } catch (err) {
        console.error('Failed to load scoring details:', err);
      } finally {
        setScoringDetailsLoading(false);
      }
    }
  }

  async function handleDeleteJob(jobId, jobName) {
    if (!window.confirm(`Delete log entry for "${jobName}"?`)) return;
    try {
      await deleteOvernightJob(jobId);
      onRefresh();
    } catch (err) {
      console.error('Failed to delete job:', err);
    }
  }

  function formatDuration(ms) {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  }

  function formatTime(ts) {
    if (!ts) return '-';
    return new Date(ts).toLocaleTimeString('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  }

  function getResultSummary(job) {
    const r = job.result;
    if (!r) return null;

    // Birthday automation results
    if (job.jobKey === 'birthdayAutomation') {
      return `${r.clientsWithDob || 0} DOBs checked, ${r.dobsDecrypted || 0} decrypted, ${r.clientsFound || 0} birthdays found, ${r.smsQueued || 0} SMS queued, ${r.emailsQueued || 0} emails queued`;
    }
    // Welcome automation
    if (job.jobKey === 'welcomeAutomation') {
      if (r.day1) {
        const total = (r.day1?.smsQueued || 0) + (r.day30?.smsQueued || 0) + (r.day90?.smsQueued || 0);
        const emails = (r.day1?.emailsQueued || 0) + (r.day30?.emailsQueued || 0) + (r.day90?.emailsQueued || 0);
        return `Day1: ${r.day1?.clientsFound || 0}, Day30: ${r.day30?.clientsFound || 0}, Day90: ${r.day90?.clientsFound || 0} clients | ${total} SMS, ${emails} emails queued`;
      }
      return `${job.recordsProcessed || 0} processed`;
    }
    // OEP automation
    if (job.jobKey === 'oepQueuePopulation') {
      if (r.skipped) return `Skipped: ${r.reason}`;
      return `${r.smsQueued || r.sent || 0} SMS queued, ${r.emailsQueued || 0} emails queued`;
    }
    // Churn prediction scoring (Lambda/SQS dispatch or direct)
    if (job.jobKey === 'nightly') {
      if (r.dispatched) return `${r.dispatched} clients dispatched to Lambda (${r.batches || 0} batches)`;
      return `${r.processed || r.total || 0} clients scored`;
    }
    // Blue Button sync
    if (job.jobKey === 'nightlyBlueButton') {
      return `${r.synced || r.processed || 0} records synced`;
    }
    // Queue processor
    if (job.jobKey === 'queueProcessor') {
      return `${r.sent || 0} sent, ${r.failed || 0} failed, ${r.skipped || 0} skipped`;
    }
    // Morning briefings
    if (job.jobKey === 'morningBriefings') {
      return `${r.generated || 0} briefings, ${r.emailsSent || 0} emails sent`;
    }
    // KPI Snapshot
    if (job.jobKey === 'kpiSnapshot') {
      return `${r.snapshots || r.processed || 0} KPIs captured`;
    }
    // Generic fallback
    if (job.recordsProcessed) return `${job.recordsProcessed} records processed`;
    return null;
  }

  function renderJobGroup(title, jobs) {
    if (!jobs || jobs.length === 0) return null;
    return (
      <div className="mb-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{title}</h4>
        <div className="space-y-2">
          {jobs.map((job, i) => {
            const isSuccess = job.status === 'completed';
            const isFailed = job.status === 'failed';
            const isRunning = job.status === 'running';
            const isStale = isRunning && job.startedAt &&
              (Date.now() - new Date(job.startedAt).getTime()) > 600000;
            const summary = getResultSummary(job);
            const isExpanded = expandedJobId === job.id;

            return (
              <div
                key={job.id || `${job.jobKey}-${i}`}
                className={`rounded-lg border ${
                  isFailed ? 'bg-red-50 border-red-200' :
                  isStale ? 'bg-yellow-50 border-yellow-200' :
                  isRunning ? 'bg-blue-50 border-blue-200' :
                  'bg-white border-gray-100'
                }`}
              >
                <div
                  className="p-3 cursor-pointer hover:bg-gray-50/50 transition-colors"
                  onClick={() => handleExpandJob(job.id, job.jobKey)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {isSuccess && <CheckCircle2 size={16} className="text-green-500" />}
                      {isFailed && <AlertCircle size={16} className="text-red-500" />}
                      {isStale && <AlertTriangle size={16} className="text-yellow-500" />}
                      {isRunning && !isStale && <RefreshCw size={16} className="text-blue-500 animate-spin" />}
                      <span className="font-medium text-sm text-gray-900">{job.jobName}</span>
                      {isStale && <span className="text-xs text-yellow-600">(interrupted by restart)</span>}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span>{formatTime(job.startedAt)}</span>
                      <span className="text-gray-300">|</span>
                      <span>{formatDuration(job.durationMs)}</span>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeleteJob(job.id, job.jobName); }}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded"
                        title="Delete log entry"
                      >
                        <Trash2 size={13} />
                      </button>
                      {isExpanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
                    </div>
                  </div>
                  {summary && (
                    <p className="text-xs text-gray-600 mt-1 ml-6">{summary}</p>
                  )}
                  {isFailed && job.errorMessage && (
                    <p className="text-xs text-red-600 mt-1 ml-6">{job.errorMessage}</p>
                  )}
                </div>
                {isExpanded && (
                  <div className="px-3 pb-3 border-t border-gray-100">
                    {job.jobKey === 'nightly' ? (
                      <div className="mt-2">
                        {scoringDetailsLoading ? (
                          <div className="flex items-center gap-2 text-gray-500 py-3">
                            <RefreshCw size={14} className="animate-spin" />
                            <span className="text-xs">Loading per-client scoring details...</span>
                          </div>
                        ) : scoringDetails?.clients?.length > 0 ? (
                          <div>
                            <div className="flex items-center justify-between mb-2">
                              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                                {scoringDetails.totalScored} Clients Scored
                                {scoringDetails.dataSource && scoringDetails.dataSource !== 'today' && (
                                  <span className="ml-2 text-amber-500 font-normal normal-case">(from {scoringDetails.dataSource})</span>
                                )}
                              </p>
                              <input
                                type="text"
                                placeholder="Search clients..."
                                value={scoringSearch}
                                onChange={(e) => setScoringSearch(e.target.value)}
                                className="text-xs border border-gray-200 rounded px-2 py-1 w-48"
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>
                            <div className="max-h-96 overflow-y-auto space-y-1">
                              {scoringDetails.clients
                                .filter(c => !scoringSearch ||
                                  c.name.toLowerCase().includes(scoringSearch.toLowerCase()) ||
                                  c.agent.toLowerCase().includes(scoringSearch.toLowerCase()))
                                .map((c) => {
                                  const isClientExpanded = expandedClientId === c.clientId;
                                  const scoreColor = c.score >= 80 ? 'text-red-600' : c.score >= 50 ? 'text-orange-500' : c.score >= 30 ? 'text-yellow-600' : 'text-green-600';
                                  const changeBadge = c.change > 0 ? 'text-red-500' : c.change < 0 ? 'text-green-500' : 'text-gray-400';
                                  return (
                                    <div key={c.clientId} className="bg-gray-50 rounded border border-gray-100">
                                      <div
                                        className="flex items-center justify-between px-2 py-1.5 cursor-pointer hover:bg-gray-100 transition-colors"
                                        onClick={(e) => { e.stopPropagation(); setExpandedClientId(isClientExpanded ? null : c.clientId); }}
                                      >
                                        <div className="flex items-center gap-2 text-xs min-w-0">
                                          <span className={`font-mono font-bold w-8 text-right ${scoreColor}`}>{c.score}</span>
                                          <span className={`font-mono w-10 ${changeBadge}`}>
                                            {c.change > 0 ? '+' : ''}{c.change}
                                          </span>
                                          <span className="font-medium text-gray-900 truncate">{c.name}</span>
                                          <span className="text-gray-400 truncate hidden sm:inline">({c.agent})</span>
                                        </div>
                                        <div className="flex items-center gap-2 text-xs text-gray-400 flex-shrink-0">
                                          {c.isAuto100 && <span className="bg-red-100 text-red-700 px-1 rounded text-[10px]">AUTO-100</span>}
                                          {isClientExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                        </div>
                                      </div>
                                      {isClientExpanded && (
                                        <div className="px-3 pb-2 border-t border-gray-200 bg-white">
                                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-1 mt-2 mb-2">
                                            {Object.entries(c.categoryScores).map(([cat, val]) => (
                                              <div key={cat} className="text-center">
                                                <p className="text-[10px] text-gray-400 uppercase">{cat.slice(0, 6)}</p>
                                                <p className="text-xs font-mono font-bold text-gray-700">{typeof val === 'number' ? val.toFixed(1) : val}</p>
                                              </div>
                                            ))}
                                          </div>
                                          {c.auto100Reason && (
                                            <p className="text-xs text-red-600 font-medium mb-1">Auto-100: {c.auto100Reason}</p>
                                          )}
                                          <p className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Contributing Factors</p>
                                          <div className="space-y-0.5 max-h-48 overflow-y-auto">
                                            {c.factors.map((f, fi) => (
                                              <div key={fi} className="flex justify-between text-xs">
                                                <span className="text-gray-600 truncate mr-2">{f.name || f.code}</span>
                                                <span className={`font-mono flex-shrink-0 ${f.points > 0 ? 'text-red-500' : f.points < 0 ? 'text-green-500' : 'text-gray-400'}`}>
                                                  {f.points > 0 ? '+' : ''}{typeof f.points === 'number' ? f.points.toFixed(1) : f.points}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                            </div>
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500 py-2">No scoring data found. Scoring runs nightly at 3 AM via Lambda.</p>
                        )}
                      </div>
                    ) : job.result ? (
                      <div className="mt-2 bg-gray-50 rounded-lg p-3 space-y-1">
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Full Result</p>
                        {Object.entries(job.result).map(([key, value]) => {
                          if (key === 'errorDetails' && Array.isArray(value) && value.length > 0) {
                            return (
                              <div key={key} className="mt-2">
                                <p className="text-xs font-medium text-red-600">errorDetails ({value.length}):</p>
                                <div className="ml-3 mt-1 space-y-0.5 max-h-40 overflow-y-auto">
                                  {value.map((err, ei) => (
                                    <p key={ei} className="text-xs text-red-500 font-mono">
                                      {err.clientId?.slice(0, 8)}... {err.error}
                                    </p>
                                  ))}
                                </div>
                              </div>
                            );
                          }
                          if (typeof value === 'object' && value !== null) {
                            return (
                              <div key={key} className="mt-1">
                                <p className="text-xs font-medium text-gray-700">{key}:</p>
                                <pre className="text-xs text-gray-600 font-mono ml-3 whitespace-pre-wrap max-h-32 overflow-y-auto">
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              </div>
                            );
                          }
                          return (
                            <div key={key} className="flex gap-2 text-xs">
                              <span className="text-gray-500 font-medium">{key}:</span>
                              <span className="text-gray-800 font-mono">{String(value)}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  const allJobs = [...(overnightJobs?.overnightJobs || []), ...(overnightJobs?.daytimeJobs || [])];
  const failedCount = allJobs.filter(j => j.status === 'failed').length;
  const successCount = allJobs.filter(j => j.status === 'completed').length;

  return (
    <section className="bg-gradient-to-r from-slate-50 to-gray-50 border border-slate-200 rounded-xl shadow-md p-4 sm:p-6 mb-6 mt-8">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <h2 className="text-lg sm:text-xl font-semibold text-slate-900 flex items-center gap-2">
          <ClipboardList size={20} className="text-slate-600" />
          Overnight Job Report
          {overnightJobs && (
            <span className="ml-2 flex items-center gap-1">
              {failedCount > 0 && (
                <span className="bg-red-100 text-red-800 text-xs px-2 py-0.5 rounded-full">
                  {failedCount} failed
                </span>
              )}
              <span className="bg-green-100 text-green-800 text-xs px-2 py-0.5 rounded-full">
                {successCount}/{allJobs.length} OK
              </span>
            </span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setRunningAll(true);
              onRunAll().finally(() => setRunningAll(false));
            }}
            disabled={runningAll}
            className="px-3 py-1 text-xs bg-slate-600 text-white rounded-lg hover:bg-slate-700 disabled:opacity-50 flex items-center gap-1"
          >
            {runningAll ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
            {runningAll ? 'Running...' : 'Run All Jobs'}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onRefresh(); }}
            className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </button>
          {expanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </div>

      {expanded && (
        <div className="mt-4">
          {loading ? (
            <div className="flex items-center gap-2 text-gray-500 py-4">
              <RefreshCw size={16} className="animate-spin" />
              <span className="text-sm">Loading job results...</span>
            </div>
          ) : !overnightJobs || allJobs.length === 0 ? (
            <div className="flex items-center gap-3 py-4 px-3 bg-white/60 rounded-lg">
              <Clock size={20} className="text-gray-400" />
              <p className="text-sm text-gray-600">No overnight jobs have run yet today. Automated jobs start at 2:00 AM Eastern.</p>
            </div>
          ) : (
            <>
              {renderJobGroup('Overnight Batch (2 AM)', overnightJobs.overnightJobs)}
              {renderJobGroup('Daytime Jobs', overnightJobs.daytimeJobs)}
            </>
          )}
        </div>
      )}
    </section>
  );
}

function DrillDownModal({ title, items, type, onClose, navigate }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const pageSize = 50;

  const filtered = items?.filter(item =>
    !search || item.name?.toLowerCase().includes(search.toLowerCase())
  ) || [];

  const paginated = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const totalPages = Math.ceil(filtered.length / pageSize);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[80vh] flex flex-col mx-2 sm:mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">{title} ({filtered.length})</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X size={20} />
          </button>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <input
            type="text"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto p-4">
          {paginated.map((item, idx) => (
            <div
              key={idx}
              className="flex items-center justify-between p-3 border rounded-lg mb-2 hover:bg-gray-50"
            >
              <div>
                <div className="font-medium">{item.name}</div>
                <div className="text-sm text-gray-500">
                  {type === 'noContact' && `${item.daysSinceContact} days since contact`}
                  {type === 'moversUp' && item.primaryReason}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {type === 'noContact' && (
                  <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded text-sm">
                    +0.25
                  </span>
                )}
                {type === 'moversUp' && item.change && (
                  <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-sm">
                    +{item.change}
                  </span>
                )}
                <span className="font-semibold">{item.newScore || item.currentScore}</span>
                <button
                  onClick={() => { onClose(); navigate(`/risk?clientId=${item.clientId}`); }}
                  className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                >
                  View
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
